import React, { useRef, useState } from "react";
import { EventCard } from "@/components/rider/EventCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CommunityEvent } from "@/types/rider";
import { ChevronLeft, ChevronRight, Calendar, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface VehicleOption {
  id: string;
  name: string;
  capacity: number;
  pricing: { [key: string]: number };
  icon: string;
}

interface AddOn {
  id: string;
  description: string;
  price: number;
  enabled: boolean;
}

interface DesktopEventSelectorV2Props {
  events: CommunityEvent[];
  activeIndex: number;
  selectedEventId: string | null;
  selectedVehicleId: string | null;
  vehicleOptions: VehicleOption[];
  isImmersive: boolean;
  setIsImmersive: (value: boolean) => void;
  handleEventSelection: (eventId: string, index: number) => void;
  handlePrevCard: () => void;
  handleNextCard: () => void;
  handleCardTap: (eventId: string, index: number) => void;
  handleContinue: (selectedAddOns: AddOn[], totalPrice: number) => void;
  handleManualEntry: () => void;
  handleResetSelection: () => void;
  handleVehicleSelection: (vehicleId: string) => void;
  handleBackToVehicles: () => void;
}

export const DesktopEventSelectorV2 = ({
  events,
  activeIndex,
  selectedEventId,
  selectedVehicleId,
  vehicleOptions,
  isImmersive,
  setIsImmersive,
  handleEventSelection,
  handlePrevCard,
  handleNextCard,
  handleCardTap,
  handleContinue,
  handleManualEntry,
  handleResetSelection,
  handleVehicleSelection,
  handleBackToVehicles
}: DesktopEventSelectorV2Props) => {
  const selectedEvent = events.find(event => event.id === selectedEventId);
  const selectedVehicle = vehicleOptions.find(vehicle => vehicle.id === selectedVehicleId);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  return (
    <div className="hidden md:block container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={handleResetSelection}
          className="flex items-center text-black hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>View All Options</span>
        </button>
        <h1 className="text-3xl font-bold text-center text-black">Book A Party Bus To</h1>
        <div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 mt-16">
        <div className="relative h-[500px] flex items-center justify-center">
          <div className={`relative w-[350px] h-[450px] transition-all duration-300 ${isImmersive ? 'scale-110' : ''}`}>
            <AnimatePresence>
              {events.map((event, index) => {
                const isActive = index === activeIndex;
                const distance = Math.abs(index - activeIndex);
                const zIndex = events.length - distance;
                
                if (distance > 2) return null;

                const shadowColors = {
                  "Coachella": "rgba(255, 122, 89, 0.15)",
                  "EDC": "rgba(128, 0, 255, 0.15)",
                  "Beyond Wonderland": "rgba(0, 183, 255, 0.15)",
                  "Los Angeles Lakers": "rgba(85, 37, 130, 0.15)",
                  "Rufus Du Sol": "rgba(255, 198, 41, 0.15)",
                  "Breakaway Festival": "rgba(255, 87, 34, 0.15)"
                };
                
                const defaultShadow = "rgba(0, 0, 0, 0.1)";
                const shadowColor = shadowColors[event.name as keyof typeof shadowColors] || defaultShadow;
                
                return (
                  <motion.div
                    key={event.id}
                    className="absolute top-0 left-0 w-full h-full cursor-pointer"
                    style={{ 
                      zIndex,
                      boxShadow: `0 10px 20px ${shadowColor}`,
                      borderRadius: '24px',
                    }}
                    initial={{ 
                      x: distance * 80,
                      rotateZ: index > activeIndex ? 5 : (index < activeIndex ? -5 : 0),
                      y: distance * 25,
                      scale: 1 - (distance * 0.1),
                      opacity: 1 - (distance * 0.3)
                    }}
                    animate={{ 
                      x: distance * 80,
                      rotateZ: index > activeIndex ? 5 : (index < activeIndex ? -5 : 0),
                      y: distance * 25,
                      scale: isActive ? (isImmersive ? 1.1 : 1) : 1 - (distance * 0.1),
                      opacity: 1 - (distance * 0.3)
                    }}
                    exit={{ 
                      opacity: 0,
                      scale: 0.8,
                      transition: { duration: 0.2 }
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }}
                    onClick={() => handleCardTap(event.id, index)}
                    whileHover={{ scale: isActive ? 1.05 : 1 }}
                  >
                    <EventCard 
                      event={event} 
                      isSelected={event.id === selectedEventId} 
                      onClick={() => {}}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
          
          <div className={`absolute left-1/2 -translate-x-1/2 flex space-x-4 mt-6 transition-opacity duration-300 ${isImmersive ? 'opacity-0' : 'opacity-100'}`} style={{ bottom: '-30px' }}>
            <button 
              className="bg-black text-white p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePrevCard}
              disabled={activeIndex === 0}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button 
              className="bg-black text-white p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleNextCard}
              disabled={activeIndex === events.length - 1}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          <div className={`text-center mt-4 transition-opacity duration-300 ${isImmersive ? 'opacity-0' : 'opacity-100'}`}>
            <p className="text-sm text-gray-500">
              {selectedEventId && !selectedVehicleId ? 'Select a vehicle to continue' : 'Tap To See Vehicle Options'}
            </p>
          </div>
        </div>
        
        {selectedEventId ? (
          <EventDetailsPanel 
            selectedEvent={selectedEvent}
            selectedVehicle={selectedVehicle}
            vehicles={vehicleOptions}
            handleContinue={handleContinue}
            handleBackToVehicles={handleBackToVehicles}
            onVehicleSelect={handleVehicleSelection}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-xl text-gray-500">Select an event to see vehicle options</p>
          </div>
        )}
      </div>
      
      <div className="flex justify-center pb-8">
        <button 
          onClick={handleManualEntry}
          className="text-black underline hover:text-neon-green transition-colors text-lg"
        >
          Enter Trip Request Manually
        </button>
      </div>
    </div>
  );
};

interface VehicleSelectionPanelProps {
  vehicles: VehicleOption[];
  selectedEvent: CommunityEvent | undefined;
  onVehicleSelect: (vehicleId: string) => void;
}


interface EventDetailsPanelProps {
  selectedEvent: CommunityEvent | undefined;
  selectedVehicle: VehicleOption | undefined;
  vehicles: VehicleOption[];
  handleContinue: (selectedAddOns: AddOn[], totalPrice: number) => void;
  handleBackToVehicles: () => void;
  onVehicleSelect: (vehicleId: string) => void;
}

const EventDetailsPanel = ({ selectedEvent, selectedVehicle, vehicles, handleContinue, handleBackToVehicles, onVehicleSelect }: EventDetailsPanelProps) => {
  const [addOns, setAddOns] = useState<AddOn[]>([
    {
      id: '1',
      description: 'Ship Wrek @ The Grand After Party',
      price: 0,
      enabled: false
    },
    {
      id: '2', 
      description: 'DJ Diesel @ Big Night Live After Party',
      price: 0,
      enabled: false
    },
    {
      id: '3',
      description: 'Professional Photography Service',
      price: 0,
      enabled: false
    }
  ]);

  if (!selectedEvent) return null;

  // Extract duration from startTime (e.g., "7 Hour Reservation | Round Trip" → "7")
  const durationMatch = selectedEvent.startTime?.match(/(\d+)\s*Hour/);
  const eventDuration = durationMatch ? durationMatch[1] : '4';
  const basePrice = selectedVehicle?.pricing[eventDuration] || 0;
  const totalPrice = basePrice + addOns.filter(addon => addon.enabled && addon.price > 0).reduce((sum, addon) => sum + addon.price, 0);

  const toggleAddOn = (id: string) => {
    setAddOns(prev => prev.map(addon => 
      addon.id === id ? { ...addon, enabled: !addon.enabled } : addon
    ));
  };

  return (
    <div className="flex flex-col mt-16">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl p-6 shadow-lg"
      >

        <h2 className="text-3xl font-bold text-black mb-2">{selectedEvent.name}</h2>
        <div className="flex items-center mb-4 text-gray-600">
          <Calendar className="h-5 w-5 mr-2" />
          <span>{selectedEvent.date?.toUpperCase().replace('/', '/').replace(' ', ', ')}-2025</span>
        </div>
        
        <div className="border-t border-gray-200 my-4 pt-4">
          <p className="text-gray-700 text-lg mb-4">{selectedEvent.startTime}</p>
          
          {!selectedVehicle ? (
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-4 text-black">Choose Your Vehicle</h3>
              <div className="grid grid-cols-2 gap-3">
                {vehicles.map((vehicle) => {
                  const price = vehicle.pricing[eventDuration];
                  
                  return (
                    <motion.div
                      key={vehicle.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Card 
                        className="cursor-pointer transition-all duration-200 hover:shadow-md border-gray-200 bg-white"
                        onClick={() => onVehicleSelect(vehicle.id)}
                      >
                        <CardContent className="p-3">
                          <div className="text-center">
                            <div className="text-2xl mb-2">{vehicle.icon}</div>
                            <h4 className="text-sm font-semibold mb-1 text-black">{vehicle.name}</h4>
                            <p className="text-xs text-gray-600 mb-2">Capacity {vehicle.capacity}</p>
                            <div className="text-lg font-bold text-black">${price}</div>
                            <p className="text-xs text-gray-500">{eventDuration} Hour Reservation</p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center mb-4">
                <button 
                  onClick={handleBackToVehicles}
                  className="flex items-center text-black hover:text-gray-700 transition-colors mr-4"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  <span className="text-sm">Back to Vehicles</span>
                </button>
              </div>
              
              <p className="text-gray-700 text-lg mb-4">{selectedVehicle.name} - Capacity {selectedVehicle.capacity}</p>
              
              <div className="flex justify-between items-center mb-6 border border-gray-200 rounded-lg p-4">
                <div>
                  <p className="text-2xl font-bold text-black">${totalPrice}</p>
                  <p className="text-gray-500">Total Price</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-black">${Math.round(totalPrice / selectedVehicle.capacity)}</p>
                  <p className="text-gray-500">Per Person</p>
                </div>
              </div>
            </>
          )}

          {/* Add-ons Section - Only show for long duration events (7+ hours) and when vehicle is selected */}
          {selectedVehicle && parseInt(eventDuration) >= 7 && (
            <div className="mb-6 space-y-3">
              {selectedEvent.date?.includes('Friday') ? (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-black font-medium">Take Me To Ship Wrek @ The Grand After Party</p>
                    <p className="text-gray-600 text-sm">Free - $0</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={addOns[0]?.enabled || false}
                      onChange={() => toggleAddOn('1')}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              ) : selectedEvent.date?.includes('Saturday') ? (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-black font-medium">Take Me To DJ Diesel @ Big Night Live After Party</p>
                    <p className="text-gray-600 text-sm">Free - $0</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={addOns[1]?.enabled || false}
                      onChange={() => toggleAddOn('2')}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              ) : null}
            </div>
          )}
          
          {selectedVehicle && (
            <div className="flex justify-center">
              <Button 
                className="bg-white text-black border border-black hover:bg-black hover:text-white rounded-full py-6 px-12 text-lg 
                   group relative overflow-hidden"
                onClick={() => handleContinue(addOns.filter(addon => addon.enabled), totalPrice)}
              >
                Book Today <span className="ml-2">🚌</span>
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};